import React, { useState } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'

const LOGO_URL = 'https://protocol.mimic.fi/assets/logo.svg'
const URL = process.env.REACT_APP_SERVER_BASE_URL

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { login } = useAuth()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const response = await axios.post(`${URL}/public/users/login`, { email, password })
      const { accessToken, refreshToken } = response.data
      login({ token: accessToken, refreshToken, email, role: response.data.role })
      navigate('/dashboard')
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data?.content?.message || error.response.data)
      } else {
        toast.error('Something went wrong :(')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Root>
      <Glow />
      <Card>
        <LogoRow>
          <img src={LOGO_URL} alt="Mimic" height={22} />
          <AdminBadge>Admin</AdminBadge>
        </LogoRow>

        <Heading>Sign in</Heading>
        <Sub>Protocol administration console</Sub>

        <Form onSubmit={handleSubmit}>
          <Fields>
            <Field>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@mimic.fi"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>
            <Field>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••••"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Field>
          </Fields>

          <SubmitBtn type="submit" disabled={loading}>
            {loading ? <LoadingDots /> : 'Continue →'}
          </SubmitBtn>
        </Form>

        <Footer>
          <a href="https://mimic.fi" target="_blank" rel="noreferrer">mimic.fi ↗</a>
          <a href="https://docs.mimic.fi" target="_blank" rel="noreferrer">Docs ↗</a>
        </Footer>
      </Card>
    </Root>
  )
}

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`

const dot = keyframes`
  0%, 80%, 100% { opacity: 0; }
  40%           { opacity: 1; }
`

const LoadingDots = () => (
  <DotsWrap>
    <Dot style={{ animationDelay: '0s' }} />
    <Dot style={{ animationDelay: '0.15s' }} />
    <Dot style={{ animationDelay: '0.3s' }} />
  </DotsWrap>
)

// ─── Styles ───────────────────────────────────────────────────────────────────

const Root = styled.div`
  min-height: 100vh;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'DMSans', sans-serif;
  position: relative;
  overflow: hidden;

  /* Subtle dot grid */
  background-image: radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px);
  background-size: 28px 28px;
`

const Glow = styled.div`
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(111,92,230,0.18) 0%, transparent 70%);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  filter: blur(40px);
`

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 380px;
  padding: 36px 32px 28px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  box-shadow: 0 0 0 1px rgba(111,92,230,0.06), 0 24px 64px rgba(0,0,0,0.6);
  animation: ${fadeIn} 0.4s ease;
`

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 28px;
`

const AdminBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #a89aff;
  background: rgba(111,92,230,0.12);
  border: 1px solid rgba(111,92,230,0.2);
  border-radius: 4px;
  padding: 2px 7px;
`

const Heading = styled.h1`
  color: #ededed;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0 0 4px;
`

const Sub = styled.p`
  color: #555;
  font-size: 13px;
  margin: 0 0 24px;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0;
`

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const Label = styled.label`
  color: #777;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const Input = styled.input`
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 7px;
  padding: 0 12px;
  height: 38px;
  color: #ededed;
  font-size: 13px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  width: 100%;
  box-sizing: border-box;

  &::placeholder { color: #444; }

  &:focus {
    border-color: rgba(111,92,230,0.5);
    box-shadow: 0 0 0 3px rgba(111,92,230,0.08);
    background: rgba(255,255,255,0.05);
  }
`

const SubmitBtn = styled.button`
  width: 100%;
  height: 38px;
  background: #6f5ce6;
  color: #fff;
  border: none;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s, box-shadow 0.15s;
  letter-spacing: 0.01em;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: #5a4bd4;
    box-shadow: 0 0 20px rgba(111,92,230,0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const Footer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(255,255,255,0.05);

  a {
    color: #444;
    font-size: 12px;
    text-decoration: none;
    transition: color 0.15s;
    &:hover { color: #777; }
  }
`

const DotsWrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const Dot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #fff;
  animation: ${dot} 1.2s ease-in-out infinite;
`
